import { uploadUrl } from "../../lib/assets";
import { normalizeStoryParagraphHtml } from "../../lib/storyHtml";
import { isValidYoutubeVideoId, youtubeEmbedUrl } from "../../lib/youtube";

function isWideBlock(block) {
  return (
    block.type === "figure" ||
    block.type === "figurePair" ||
    block.type === "figureCollage" ||
    block.type === "youtube"
  );
}

/**
 * @param {{ block: object, isLastParagraph?: boolean }} props
 */
function StoryBlock({ block, isLastParagraph = false }) {
  if (block.type === "lead") {
    return <p className="story-reader__lead">{block.text}</p>;
  }

  if (block.type === "paragraph") {
    return (
      <div
        className={`story-reader__paragraph${isLastParagraph ? " story-reader__paragraph--last" : ""}`}
        dangerouslySetInnerHTML={{ __html: normalizeStoryParagraphHtml(block.html) }}
      />
    );
  }

  if (block.type === "quote") {
    return (
      <blockquote className="story-reader__quote">
        &ldquo;{block.text}&rdquo;
      </blockquote>
    );
  }

  if (block.type === "figure") {
    return (
      <figure className="story-reader__figure">
        <div
          className="story-reader__figure-image"
          style={{ backgroundImage: `url('${uploadUrl(block.imageKey)}')` }}
        />
        {block.caption ? (
          <figcaption className="story-reader__figure-caption">{block.caption}</figcaption>
        ) : null}
      </figure>
    );
  }

  if (block.type === "figurePair") {
    return (
      <figure className="story-reader__figure-pair">
        <div className="story-reader__figure-pair-grid">
          <div
            className="story-reader__figure-pair-image"
            style={{ backgroundImage: `url('${uploadUrl(block.leftImageKey)}')` }}
          />
          <div
            className="story-reader__figure-pair-image"
            style={{ backgroundImage: `url('${uploadUrl(block.rightImageKey)}')` }}
          />
        </div>
        {block.caption ? (
          <figcaption className="story-reader__figure-caption">{block.caption}</figcaption>
        ) : null}
      </figure>
    );
  }

  if (block.type === "figureCollage" && Array.isArray(block.images) && block.images.length) {
    return (
      <figure className="story-reader__figure-collage">
        {block.heading ? (
          <div className="story-reader__figure-collage-heading">{block.heading}</div>
        ) : null}
        <div className="story-reader__figure-collage-grid">
          {block.images.map((item) => (
            <div
              key={item.imageKey}
              className="story-reader__figure-collage-image"
              style={{ backgroundImage: `url('${uploadUrl(item.imageKey)}')` }}
            />
          ))}
        </div>
        {block.caption ? (
          <figcaption className="story-reader__figure-caption">{block.caption}</figcaption>
        ) : null}
      </figure>
    );
  }

  if (block.type === "youtube" && isValidYoutubeVideoId(block.videoId)) {
    return (
      <figure className="story-reader__figure story-reader__figure--video">
        <div className="story-reader__video">
          <iframe
            src={youtubeEmbedUrl(block.videoId)}
            title={block.caption || "YouTube video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            loading="lazy"
          />
        </div>
        {block.caption ? (
          <figcaption className="story-reader__figure-caption">{block.caption}</figcaption>
        ) : null}
      </figure>
    );
  }

  return null;
}

/**
 * @param {object[]} blocks
 */
function groupBlocksForLayout(blocks) {
  /** @type {Array<{ kind: 'body', items: { block: object, index: number }[] } | { kind: 'wide', block: object, index: number }>} */
  const groups = [];
  /** @type {{ block: object, index: number }[]} */
  let bodyRun = [];

  function flushBody() {
    if (!bodyRun.length) return;
    groups.push({ kind: "body", items: bodyRun });
    bodyRun = [];
  }

  blocks.forEach((block, index) => {
    if (isWideBlock(block)) {
      flushBody();
      groups.push({ kind: "wide", block, index });
      return;
    }
    bodyRun.push({ block, index });
  });
  flushBody();

  return groups;
}

/**
 * @param {{ body: object[] }} props
 */
export default function StoryContent({ body }) {
  const blocks = Array.isArray(body) ? body : [];
  const lastParagraphIndex = blocks.reduce(
    (lastIndex, block, index) => (block.type === "paragraph" ? index : lastIndex),
    -1
  );
  const groups = groupBlocksForLayout(blocks);

  return (
    <>
      {groups.map((group, groupIndex) => {
        if (group.kind === "wide") {
          return <StoryBlock key={`wide-${group.index}`} block={group.block} />;
        }

        return (
          <div key={`body-${groupIndex}`} className="story-reader__body">
            {group.items.map(({ block, index }) => (
              <StoryBlock
                key={`block-${index}`}
                block={block}
                isLastParagraph={block.type === "paragraph" && index === lastParagraphIndex}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}
